import React from 'react';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import UpgradePrompt from './UpgradePrompt';

/**
 * ProFeature wrapper component for conditional rendering of Pro features
 * 
 * @param {Object} props
 * @param {string} props.feature - The feature name to check access for
 * @param {React.ReactNode} props.children - The Pro feature content to render if user has access
 * @param {React.ReactNode} [props.fallback] - Alternative content to render if user doesn't have access
 * @param {string} [props.upgradeTitle] - Custom title for the upgrade prompt
 * @param {string} [props.upgradeDescription] - Custom description for the upgrade prompt
 * @param {boolean} [props.showUpgradePrompt=true] - Whether to show upgrade prompt when user doesn't have access
 * @returns {React.ReactElement}
 */
function ProFeature({ 
  feature, 
  children, 
  fallback, 
  upgradeTitle, 
  upgradeDescription,
  upgradeSource,
  showUpgradePrompt = true 
}) {
  const { hasAccess } = useFeatureAccess();

  // If user has access to the feature, render the children
  if (hasAccess(feature)) {
    return children;
  }

  // If a custom fallback is provided, render it
  if (fallback) {
    return fallback;
  }

  // If upgrade prompt is disabled, render nothing
  if (!showUpgradePrompt) {
    return null;
  }

  // Otherwise, render the upgrade prompt
  return (
    <UpgradePrompt 
      feature={upgradeTitle || feature}
      description={upgradeDescription}
      source={upgradeSource || 'pro_feature'}
    />
  );
}

export default ProFeature; 